<template>
  <section class="hero">
    <div class="hero-content">
      <div class="badge reveal">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="gold-svg"
        >
          <path
            d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"
          />
        </svg>
        Revolutionizing Business Admin
      </div>
      <h1 class="reveal delay-1">
        Your AI Assistant That Actually<br /><span
          >Understands Your Business</span
        >
      </h1>
      <p class="subtitle reveal delay-2">
        Stop juggling admin tasks. Start growing your revenue. <br /><strong
          >Save 10+ hours every week</strong
        >
        with intelligent automation.
      </p>

      <div class="cta-group reveal delay-3">
        <a href="#waitlist" class="cta-button">Join the Waitlist</a>
        <div class="trust-line">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          Private. Secure. Built for growth.
        </div>
      </div>
    </div>

    <div class="hero-visual">
      <div class="demo-grid">
        <div class="demo-card glass-card reveal delay-1">
          <div class="card-header">
            <div class="icon-frame">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
              </svg>
            </div>
            <h3>Operational Excellence</h3>
          </div>
          <p class="card-desc">Handling inquiries 24/7</p>
          <div class="chat-mockup">
            <img
              src="../assets/images/pabu-chat-demo.gif"
              alt="Bizzento Chat"
              class="chat-img"
            />
          </div>
        </div>

        <div class="demo-card glass-card reveal delay-2">
          <div class="card-header">
            <div class="icon-frame">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
                <path d="M22 12A10 10 0 0 0 12 2v10z" />
              </svg>
            </div>
            <h3>Strategic Growth</h3>
          </div>
          <p class="card-desc">Real-time business insights</p>
          <div class="dashboard-mockup">
            <div class="metric">
              <span class="label">Revenue</span>
              <span class="value">$4,800 <small>↑ 22%</small></span>
            </div>
            <div class="metric">
              <span class="label">Bookings</span>
              <span class="value">32 <small>↑ 15%</small></span>
            </div>
            <div class="insight-pill">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="pill-svg"
              >
                <path
                  d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .5 2.2 1.5 3.1.7.7 1.3 1.5 1.5 2.4"
                />
                <path d="M9 18h6" />
                <path d="M10 22h4" />
              </svg>
              Trend: High interest in "Chocolate Cake"
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.hero {
  --bg-warm-base: #f2eee0;
  --sunlight-glow: #faefca;
  --soft-gold: #f5e5b0;
  --dark-accent: #1a1a1a;
  --text-muted: #8e8a82;
  --gold-highlight: #bfa14d;

  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 120px 24px 80px;
  text-align: center;
  color: var(--dark-accent);
  background-color: var(--bg-warm-base);
  background-image: radial-gradient(
      at 0% 0%,
      var(--sunlight-glow) 0%,
      transparent 55%
    ),
    radial-gradient(at 100% 100%, var(--soft-gold) 0%, transparent 55%);
}

.hero-content {
  max-width: 900px;
  margin-bottom: 80px;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: white;
  border: 1px solid var(--soft-gold);
  border-radius: 100px;
  font-size: 0.85rem;
  font-weight: 700;
  margin-bottom: 32px;
}

.gold-svg {
  color: var(--gold-highlight);
}

h1 {
  font-size: clamp(2.5rem, 6vw, 4.2rem);
  line-height: 1.05;
  margin-bottom: 24px;
  font-weight: 800;
  letter-spacing: -0.02em;
}

h1 span {
  color: var(--gold-highlight);
}

.subtitle {
  font-size: 1.25rem;
  color: var(--text-muted);
  line-height: 1.6;
  max-width: 700px;
  margin: 0 auto 48px;
}

.cta-button {
  display: inline-block;
  background: var(--dark-accent);
  color: white;
  padding: 20px 48px;
  border-radius: 100px;
  font-weight: 700;
  text-decoration: none;
  transition: all 0.3s ease;
}

.cta-button:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
}

.trust-line {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 20px;
  font-size: 0.9rem;
  color: var(--text-muted);
}

/* Visuals Styling */
.demo-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  gap: 32px;
  width: 100%;
  max-width: 1100px;
}

.glass-card {
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 32px;
  padding: 40px;
  text-align: left;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.03);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
}

.icon-frame {
  width: 48px;
  height: 48px;
  background: var(--sunlight-glow);
  color: var(--gold-highlight);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  flex-shrink: 0;
}

.card-header h3 {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 700;
}

.card-desc {
  color: var(--text-muted);
  margin-bottom: 32px;
  font-size: 1rem;
}

.metric {
  display: flex;
  justify-content: space-between;
  padding: 16px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.metric .value {
  color: #2d6a4f;
  font-weight: 700;
}

.insight-pill {
  display: flex;
  align-items: center;
  margin-top: 24px;
  background: var(--sunlight-glow);
  padding: 14px 18px;
  border-radius: 16px;
  font-size: 0.85rem;
  font-weight: 700;
  color: #856404;
}

.pill-svg {
  margin-right: 10px;
}

.chat-img {
  width: 100%;
  border-radius: 16px;
}

@media (max-width: 768px) {
  .hero {
    padding: 100px 20px 60px;
  }
  .demo-grid {
    grid-template-columns: 1fr;
  }
}
</style>
